import React, { useState, useEffect, useRef } from "react";
import Card from "../common/Card";

const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const sectionRef = useRef(null);
  const autoSlideRef = useRef(null);

  const testimonials = [
    {
      id: 1,
      name: "Najib",
      role: "Food Critic",
      comment:
        "One of the best dining experiences I've had this year. The flavors were exceptional!",
      rating: 5,
      avatar: "NC",
    },
    {
      id: 2,
      name: "Asif",
      role: "Regular Customer",
      comment:
        "Consistently excellent food and service. The wagyu burger is to die for!",
      rating: 5,
      avatar: "AR",
    },
    {
      id: 3,
      name: "Hari",
      role: "First-time Visitor",
      comment:
        "Beautiful ambiance and incredible attention to detail.",
      rating: 5,
      avatar: "HS",
    },
    {
      id: 4,
      name: "Salih",
      role: "Food Blogger",
      comment:
        "Every dish tells a story. The culinary journey here is absolutely remarkable!",
      rating: 5,
      avatar: "SM",
    },
    {
      id: 5,
      name: "Ahmed",
      role: "Business Executive",
      comment:
        "Perfect for business dinners. Impeccable service and exquisite presentation.",
      rating: 5,
      avatar: "AK",
    },
    {
      id: 6,
      name: "Layla",
      role: "Local Foodie",
      comment:
        "Found my new favorite spot! The flavors are authentic yet innovative.",
      rating: 5,
      avatar: "LN",
    },
  ];

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    if (isPaused) {
      // Clear interval when paused
      if (autoSlideRef.current) {
        clearInterval(autoSlideRef.current);
        autoSlideRef.current = null;
      }
      return;
    }

    // Start auto-sliding
    autoSlideRef.current = setInterval(() => {
      setActiveIndex((prev) => {
        return (prev + 1) % testimonials.length;
      });
    }, 5000);

    // Cleanup on unmount
    return () => {
      if (autoSlideRef.current) {
        clearInterval(autoSlideRef.current);
        autoSlideRef.current = null;
      }
    };
  }, [isPaused, testimonials.length]);

  const goToSlide = (index) => {
    setActiveIndex(index);

    // Reset auto-slide timer when manually navigating
    if (autoSlideRef.current) {
      clearInterval(autoSlideRef.current);
      autoSlideRef.current = null;
    }

    // Restart auto-slide after a delay if not paused
    if (!isPaused) {
      const timer = setTimeout(() => {
        if (!isPaused) {
          autoSlideRef.current = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % testimonials.length);
          }, 5000);
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  };

  const nextSlide = () => {
    const nextIndex = (activeIndex + 1) % testimonials.length;
    goToSlide(nextIndex);
  };

  const prevSlide = () => {
    const prevIndex =
      (activeIndex - 1 + testimonials.length) % testimonials.length;
    goToSlide(prevIndex);
  };

  const renderStars = (rating) => {
    return (
      <div className="flex justify-center gap-1">
        {[...Array(5)].map((_, i) => (
          <svg
            key={i}
            className={`w-5 h-5 transition-all duration-300 ${
              i < rating ? "text-yellow-400" : "text-gray-300"
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-gray-50 to-white overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="container mx-auto px-4">
        {/* Section Header with Animation */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-display font-bold text-secondary mb-6">
            What Our <span className="text-primary">Guests</span> Say
          </h2>
          <p className="text-lg text-gray-600 font-body max-w-2xl mx-auto leading-relaxed">
            Don't just take our word for it - hear from our delighted guests
            who've experienced our culinary excellence
          </p>
        </div>

        {/* Testimonials Slider */}
        <div className="relative max-w-6xl mx-auto">
          {/* Slider Container */}
          <div className="overflow-hidden rounded-2xl">
            <div
              className="flex transition-transform duration-700 ease-out"
              style={{
                transform: `translateX(-${activeIndex * 100}%)`,
                willChange: "transform",
              }}
            >
              {testimonials.map((testimonial, index) => (
                <div key={testimonial.id} className="w-full flex-shrink-0 px-4">
                  <div
                    className={`transition-all duration-700 ${
                      isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
                    }`}
                  >
                    <Card className="relative overflow-hidden group h-full p-8 md:p-12 bg-white shadow-xl hover:shadow-2xl transition-all duration-500">
                      {/* Animated Background Pattern */}
                      <div className="absolute inset-0 opacity-5">
                        <div className="absolute -top-20 -right-20 w-40 h-40 bg-primary rounded-full blur-3xl"></div>
                        <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-secondary rounded-full blur-3xl"></div>
                      </div>

                      {/* Quote Icon with Animation */}
                      <div className="absolute top-8 right-8 text-primary/10 group-hover:text-primary/20 transition-all duration-700 group-hover:scale-125 group-hover:rotate-12">
                        <svg
                          className="w-16 h-16"
                          fill="currentColor"
                          viewBox="0 0 32 32"
                        >
                          <path d="M10 8c-3.3 0-6 2.7-6 6v10h10V14H6.5C6.8 10.9 8.1 8.4 10 8z M22 8c-3.3 0-6 2.7-6 6v10h10V14h-7.5c.3-3.1 1.6-5.6 3.5-5.6z" />
                        </svg>
                      </div>

                      {/* Content Container */}
                      <div className="relative z-10">
                        {/* Avatar and Info with Staggered Animation */}
                        <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-8">
                          <div className="relative">
                            <div className="w-20 h-20 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white font-display font-bold text-2xl group-hover:scale-110 transition-all duration-500 shadow-lg">
                              {testimonial.avatar}
                            </div>
                            {/* Animated Ring */}
                            <div className="absolute inset-0 rounded-full border-2 border-primary/30 animate-ping-slow opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                          </div>

                          <div className="text-center md:text-left">
                            <h4 className="font-display font-bold text-secondary text-2xl group-hover:text-primary transition-colors duration-300 mb-2">
                              {testimonial.name}
                            </h4>
                            <p className="text-gray-600 font-body text-lg">
                              {testimonial.role}
                            </p>
                          </div>
                        </div>

                        {/* Stars with Animation */}
                        <div className="mb-8 transform group-hover:scale-105 transition-all duration-500 flex justify-center md:justify-start">
                          {renderStars(testimonial.rating)}
                        </div>

                        {/* Comment with Slide-in Animation */}
                        <div className="relative mb-8">
                          <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-3/4 bg-gradient-to-b from-primary to-secondary rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700"></div>
                          <p className="text-gray-800 font-body leading-relaxed text-xl md:text-2xl italic pl-6 group-hover:translate-x-2 transition-transform duration-500">
                            "{testimonial.comment}"
                          </p>
                        </div>

                        {/* Animated Decorative Line */}
                        <div className="relative h-1 bg-gradient-to-r from-gray-200 to-gray-200 rounded-full overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000"></div>
                        </div>
                      </div>

                      {/* Corner Decorations */}
                      <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-primary/30 rounded-tl-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-secondary/30 rounded-br-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    </Card>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center justify-center mt-12 space-x-8">
            {/* Previous Button */}
            <button
              onClick={prevSlide}
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              className="group relative w-14 h-14 rounded-full bg-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110 focus:outline-none"
              aria-label="Previous testimonial"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <svg
                className="relative w-6 h-6 text-gray-600 group-hover:text-white transition-colors duration-300 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Slide Indicators */}
            <div className="flex items-center space-x-3">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  onMouseEnter={() => setIsPaused(true)}
                  onMouseLeave={() => setIsPaused(false)}
                  className="focus:outline-none group"
                  aria-label={`Go to testimonial ${index + 1}`}
                >
                  <div className="relative">
                    {/* Active Indicator */}
                    <div
                      className={`w-3 h-3 rounded-full transition-all duration-500 ${
                        index === activeIndex
                          ? "bg-gradient-to-r from-primary to-secondary scale-125"
                          : "bg-gray-300 group-hover:bg-gray-400"
                      }`}
                    ></div>

                    {/* Hover Effect */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary to-secondary opacity-0 group-hover:opacity-30 transition-opacity duration-300 scale-150"></div>

                    {/* Pulsing Effect for Active Slide */}
                    {index === activeIndex && !isPaused && (
                      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary to-secondary animate-ping-slow opacity-20"></div>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Next Button */}
            <button
              onClick={nextSlide}
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              className="group relative w-14 h-14 rounded-full bg-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110 focus:outline-none"
              aria-label="Next testimonial"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <svg
                className="relative w-6 h-6 text-gray-600 group-hover:text-white transition-colors duration-300 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes pingSlow {
          0%,
          100% {
            transform: scale(1);
            opacity: 0.5;
          }
          50% {
            transform: scale(1.5);
            opacity: 0;
          }
        }

        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }

        .animate-slide-in-left {
          animation: slideInLeft 0.8s ease-out forwards;
        }

        .animate-slide-in-right {
          animation: slideInRight 0.8s ease-out forwards;
        }

        .animate-ping-slow {
          animation: pingSlow 3s cubic-bezier(0, 0, 0.2, 1) infinite;
        }

        /* Smooth transitions */
        * {
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Custom scrollbar for slider container */
        .overflow-hidden::-webkit-scrollbar {
          display: none;
        }

        /* Hide scrollbar for IE, Edge and Firefox */
        .overflow-hidden {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
};

export default Testimonials;
